import { Switch } from 'antd'
import { NAMESPACE } from 'locale'
import { reportsConstants } from 'locale/team'
import { Fragment, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import type { CapabilityKey, ToggleConfig } from './types'
import { useServiceUserNominative } from './useServiceUserNominative'

export type CapabilityToggleListProps = {
  toggles: ToggleConfig[]
  value: Partial<Record<CapabilityKey, boolean>>
  onChange: (key: CapabilityKey, next: boolean) => void
  testId?: string
  // Optional content rendered immediately after a given toggle's card (e.g. an
  // inline warning that belongs directly under that capability). Keyed by the
  // toggle's CapabilityKey.
  renderAfter?: Partial<Record<CapabilityKey, ReactNode>>
  // Toggle cards to render in the danger/error state (red border).
  errorKeys?: CapabilityKey[]
}

const ToggleRow = styled.div<{ $error?: boolean }>`
  background: ${({ theme }) => theme.color.neutral.color10};
  border: 1px solid
    ${({ theme, $error }) =>
      $error ? theme.color.status.danger : theme.color.neutral.color8};
  border-radius: 16px;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ToggleHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`

const ToggleLabel = styled.div`
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.color.neutral.color2};
`

const SwitchGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const SwitchLabel = styled.span`
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.color.neutral.color2};
`

const Bullets = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Bullet = styled.li<{ $cross?: boolean }>`
  display: flex;
  gap: 8px;
  color: ${({ theme }) => theme.color.neutral.color5};
  font-size: 16px;
  line-height: 24px;
  &::before {
    content: ${({ $cross }) => ($cross ? "'✕'" : "'✓'")};
    color: ${({ theme, $cross }) =>
      $cross ? theme.color.status.danger : theme.color.status.success};
    font-weight: 700;
  }
`

export const CapabilityToggleList = ({
  toggles,
  value,
  onChange,
  testId,
  renderAfter,
  errorKeys
}: CapabilityToggleListProps) => {
  const { t } = useTranslation(NAMESPACE.TEAM)
  const nominative = useServiceUserNominative()

  return (
    <>
      {toggles.map((cfg) => {
        const checked = value[cfg.key] === true
        const hasError = errorKeys?.includes(cfg.key) === true
        return (
          <Fragment key={cfg.key}>
          <ToggleRow
            $error={hasError}
            data-error={hasError || undefined}
            data-toggle-key={cfg.key}
          >
            <ToggleHeader>
              <ToggleLabel>
                {t(cfg.labelKey, { serviceUser: nominative })}
              </ToggleLabel>
              <SwitchGroup>
                <SwitchLabel>
                  {checked
                    ? t(reportsConstants.CAPABILITY_TOGGLE_ON)
                    : t(reportsConstants.CAPABILITY_TOGGLE_OFF)}
                </SwitchLabel>
                <Switch
                  checked={checked}
                  aria-label={t(cfg.labelKey, { serviceUser: nominative })}
                  onChange={(next) => onChange(cfg.key, next)}
                  data-testid={
                    testId ? `${testId}-${cfg.key}-switch` : undefined
                  }
                />
              </SwitchGroup>
            </ToggleHeader>
            <Bullets>
              {cfg.bullets.map((b, idx) => (
                <Bullet
                  // biome-ignore lint/suspicious/noArrayIndexKey: bullets are a static list derived from a constant TOGGLE_DEFS definition — never reordered or filtered
                  key={`${cfg.key}-bullet-${idx}`}
                  $cross={b.variant === 'cross'}
                >
                  {t(b.copyKey, { serviceUser: nominative })}
                </Bullet>
              ))}
            </Bullets>
          </ToggleRow>
          {renderAfter?.[cfg.key]}
          </Fragment>
        )
      })}
    </>
  )
}
